import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PageTitle, Table, useList, CARD, INPUT, BTN, BTN_GHOST } from "@/pages/admin/ui";
import { Plus, Pencil, Trash2, CheckCircle2, CircleDashed } from "lucide-react";

const EMPTY = { name: "", description: "", price: 0, category: "", cuisine: "", image_url: "", dietary: "Pure Veg, Jain", is_available: true, is_featured: false, is_bestseller: false, status: "published", order: 0 };

export default function MenuAdmin() {
  const { data, loading, reload } = useList("/admin/menu", []);
  const { data: cats } = useList("/admin/menu/categories", []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const full = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image_url: full }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const openNew = () => { setForm({ ...EMPTY }); setOpen(true); };
  const openEdit = (row) => { setForm({ ...row, dietary: (row.dietary || []).join(", ") }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: Number(form.price), order: Number(form.order || 0), dietary: (form.dietary || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (form.id) await api.put(`/admin/menu/${form.id}`, payload);
      else await api.post("/admin/menu", payload);
      toast.success("Menu updated — live on the website");
      setOpen(false); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (row) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/admin/menu/${row.id}`); toast.success("Deleted"); reload(); };
  const togglePublish = async (row) => {
    const payload = { ...row, status: row.status === "published" ? "draft" : "published" };
    await api.put(`/admin/menu/${row.id}`, payload); toast.success(payload.status === "published" ? "Published" : "Moved to draft"); reload();
  };

  return (
    <div data-testid="admin-menu">
      <PageTitle title="Menu Management" subtitle="Changes reflect on the public website instantly. Draft → Publish workflow." right={<button className={BTN} onClick={openNew} data-testid="menu-add"><Plus className="h-4 w-4" /> Add Item</button>} />
      {loading ? <div className="text-sm text-[#64748B]">Loading…</div> : (
        <Table headers={["Item", "Category", "Cuisine", "Price", "Dietary", "Status", "Live", "Actions"]}>
          {data.map((r) => (
            <tr key={r.id} className="hover:bg-[#F8F9FA]">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {r.image_url && <img src={r.image_url} alt="" className="h-10 w-10 rounded object-cover" />}
                  <div><div className="font-medium text-[#0F172A]">{r.name}</div>{(r.is_bestseller || r.is_featured) && <div className="text-[10px] uppercase tracking-wider text-[#C5A059]">{r.is_bestseller ? "Bestseller " : ""}{r.is_featured ? "Featured" : ""}</div>}</div>
                </div>
              </td>
              <td className="px-4 py-3 text-[#334155]">{r.category}</td>
              <td className="px-4 py-3 text-[#334155]">{r.cuisine}</td>
              <td className="px-4 py-3 text-[#334155]">₹{r.price}</td>
              <td className="px-4 py-3 text-xs text-[#64748B]">{(r.dietary || []).join(", ")}</td>
              <td className="px-4 py-3">
                <button onClick={() => togglePublish(r)} data-testid="menu-toggle-publish" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${r.status === "published" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {r.status === "published" ? <CheckCircle2 className="h-3 w-3" /> : <CircleDashed className="h-3 w-3" />} {r.status}
                </button>
              </td>
              <td className="px-4 py-3 text-xs">{r.is_available ? <span className="text-green-600">Available</span> : <span className="text-red-500">Hidden</span>}</td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <button className={BTN_GHOST + " !p-2"} onClick={() => openEdit(r)} data-testid="menu-edit"><Pencil className="h-4 w-4" /></button>
                  <button className={BTN_GHOST + " !p-2 text-red-600"} onClick={() => remove(r)} data-testid="menu-delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{form?.id ? "Edit" : "Add"} Menu Item</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4" data-testid="menu-form">
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Name</label>
                <input required className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="menu-name" /></div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Description</label>
                <textarea rows={2} className={INPUT} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Price (₹)</label>
                  <input type="number" className={INPUT} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="menu-price" /></div>
                <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Cuisine</label>
                  <input className={INPUT} value={form.cuisine} onChange={(e) => setForm({ ...form, cuisine: e.target.value })} /></div>
              </div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className={INPUT} data-testid="menu-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{(cats || []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Dish Photo</label>
                <div className="mt-1 flex items-center gap-3">
                  {form.image_url && <img src={form.image_url} alt="" className="h-16 w-16 rounded object-cover border border-[#E2E8F0]" />}
                  <label className="cursor-pointer rounded-md border border-dashed border-[#C5A059] px-4 py-2 text-sm font-medium text-[#C5A059] hover:bg-[#C5A059]/5">
                    {uploading ? "Uploading…" : "Upload Photo"}
                    <input type="file" accept="image/*" className="hidden" data-testid="menu-image-upload" onChange={(e) => uploadImage(e.target.files?.[0])} />
                  </label>
                </div>
                <input className={INPUT + " mt-2"} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="…or paste an image URL" /></div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Dietary (comma separated)</label>
                <input className={INPUT} value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })} placeholder="Pure Veg, Jain, Spicy" /></div>
              <div className="grid grid-cols-3 gap-3">
                {[["is_available", "Available"], ["is_featured", "Featured"], ["is_bestseller", "Bestseller"]].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-[#64748B]"><Switch checked={!!form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} /> {l}</label>
                ))}
              </div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className={INPUT}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="published">Published</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
                </Select></div>
              <div className="flex justify-end gap-2 pt-2"><button type="button" className={BTN_GHOST} onClick={() => setOpen(false)}>Cancel</button>
                <button type="submit" className={BTN} disabled={saving} data-testid="menu-save">{saving ? "Saving…" : "Save"}</button></div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
